import { tracker } from '@jctrans-materials/shared'
import gioPerformance from 'gio-webjs-sdk/plugins/gioPerformance'
import { getAppId } from '~/utils/auth'

export const gioPageReport = (data: any, headers: any) => {
  try {
    if (typeof window !== 'undefined') {
      let eventName =
        data.eventCode || data.eventId || data.eventName || data.event || null

      if (eventName) {
        eventName = eventName.replace(/\./g, '_')

        const rawExtensions = data.extensions || {}
        const gdpAttributes: any = {}

        // 将 extensions 里的对象值压缩成 JSON string 防止 GIO 无法识别嵌套对象
        Object.keys(rawExtensions).forEach((key) => {
          const val = rawExtensions[key]

          // 如果值是对象或数组，强制转为 JSON 字符串
          if (val !== null && typeof val === 'object') {
            gdpAttributes[key] = JSON.stringify(val)
          } else {
            gdpAttributes[key] = val
          }
        })
        gdpAttributes['report-headers'] = JSON.stringify(headers)
        // page code => 扩展属性

        const pageCode =
          headers['page-code'] || headers['PAGE-CODE'] || 'PAGE-CODE-IS-NULL'
        gdpAttributes['PAGE_CODE'] = pageCode
        // 发送埋点
        tracker.setPageAttributes({
          ...gdpAttributes,
          pageCode_var: pageCode,
        })
        tracker.sendPage()
        console.log(`✅ GIO Event Tracked: ${eventName}`, gdpAttributes)
      }
    }
  } catch (err) {
    console.error('⚠️ GDP track error:', err)
  }
}

/**
 * 2. 设置登录用户 ID
 * @param {string} userId - 用户唯一标识 (1-1000字符)
 * @param {string} userKey - 用户身份类型 (需在数据中心预先定义)
 */
export const setUserId = (userId: any, userKey = null) => {
  if (typeof window === 'undefined') {
    console.error('Gio SDK 未初始化')
    return
  }

  if (!userId) {
    console.warn('setUserId 失败: userId 不能为空')
    return
  }

  // 长度限制处理：超过1000长度只截取前1000
  const finalUserId = String(userId).substring(0, 1000)

  if (userKey) {
    // 如果传了 userKey，适用于 ID-MAPPING
    tracker.setUserId(finalUserId, userKey, () => {
      console.log('👤 UserID Set Done with Key')
    })
    console.log(`👤 UserID Set: ${finalUserId} (Key: ${userKey})`)
  } else {
    // 默认设置
    tracker.setUserId(finalUserId, () => {
      console.log('👤 UserID Set Done')
    })
    console.log(`👤 UserID Set: ${finalUserId}`)
  }
}

/**
 * 3. 清除用户 ID (通常用于退出登录)
 */
export const clearUserId = () => {
  if (typeof window !== 'undefined') {
    tracker.clearUserId(() => {
      console.log('👤 UserID Cleared Done')
    })
    tracker.clearGeneralProps() // 同步清除通用属性，防止脏数据
    tracker.setGeneralProps({
      projectID_var: getPrjId() || '-',
      appId: getAppId(),
      userId: '-',
      company_id_ppl: '-',
      companyId: '-',
      userNameCn: '-',
      basic_name_CN: '-',
      userNameEn: '-',
      basic_name_EN: '-',
      companyNameCn: '-',
      companyNameEn: '-',
      members: '-',
      account: '-',
    })
  }
}

/**
 * 4. 设置通用属性 (这些属性会自动附加到所有事件上)
 * @param params
 * @param cb
 * @returns
 */
export const setGeneralProps = (params: any, cb: any) => {
  if (typeof window === 'undefined') {
    console.error('Gio SDK 未初始化')
    return
  }

  tracker.setGeneralProps(params, () => {
    console.log('🔧 General Props Set Done')
    if (cb) cb()
  })
}

/**
 * 注册 GIO 相关插件
 */
export const registerGioPlugins = () => {
  if (typeof window !== 'undefined') {
    const isProd = window.location.hostname.includes('jctrans.com')

    if (isProd) {
      tracker.registerPlugins([gioPerformance])
    } else {
      console.warn('⚠️ 非生产环境，未注册 GIO 性能插件')
    }
  }
}

const ERA_GIO_KEY_MAP: any = {
  ERA_GIO_INQUIRY_ID: 'inquiryID_var', // 询盘 ID
  ERA_GIO_COMPANY_ID: 'shopID_var', // 商铺 ID
  ERA_GIO_BUSINESS_OPT_ID: 'businessOptID_var', // 商机 ID
  ERA_GIO_ROUTE_SERVICE_DETAIL_ID: 'routeID_var', // 航线 ID
}

/**
 * 核心复用：格式化并清洗埋点扩展属性 (Click/Custom 埋点皆可复用此方法)
 * @param {Object} extensions 原始扩展属性
 * @returns {Object} 清洗后符合 GIO 规范的属性对象
 */
export const formatGioAttributes = (extensions: any = {}) => {
  const gdpAttributes: any = {}

  Object.keys(extensions).forEach((key) => {
    const val = extensions[key]

    // 1. 复杂对象/数组降维（防止 GIO 序列化失败抛错）
    if (val !== null && typeof val === 'object') {
      gdpAttributes[key] = JSON.stringify(val)
      return // 提前 continue
    }

    // 2. 映射特定业务 Key
    if (key.startsWith('ERA_GIO_')) {
      const varName = ERA_GIO_KEY_MAP[key] || key
      // 使用三元表达式防脏数据，兼容 0 的情况，过滤空字符串、null、undefined
      gdpAttributes[varName] =
        val !== '' && val !== null && val !== undefined ? val : '-'
      return // 提前 continue
    }

    // 3. 常规普通属性直接赋值
    gdpAttributes[key] = val
  })

  return gdpAttributes
}
